// 調整舞台畫面共用的小零件：
//   BlockButton  不能按的按鈕：灰的、滑鼠提示說原因，點一下（觸控沒有滑鼠提示）用 toast 講原因；畫面上不常駐那一行字
//   TipButton    小「?」：解釋平常收著，點了才在下面打開
//   PanelSection 面板的一段：標題可以收合，每位使用者記住自己的開合（lib/adjust/sectionState.ts）
//   ScrubNumber  可拖的數字（像 After Effects）：游標放在數字上左右拖就改值，點一下才變輸入框

import { useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { ChevronRight, CircleHelp } from 'lucide-react';
import Icon from '../Icon';
import { toast } from '../../lib/toast';
import { useViewAsUser } from '../../lib/viewAsContext';
import { SCRUB_THRESHOLD_PX, scrubSeconds } from '../../lib/adjust/scrub';
import { parseTimingSeconds } from '../../lib/mechanisms/model';
import { isSectionOpen, sectionMap, setSectionOpen, subscribeSections, type SectionId } from '../../lib/adjust/sectionState';

export function BlockButton({ reason, className, title, onClick, children, label, pressed, disabled }: {
  /** 有給 = 不能按，這句是原因 */
  reason?: string | null;
  className?: string;
  title?: string;
  onClick: () => void;
  children: ReactNode;
  /** 只有圖示的按鈕要給 */
  label?: string;
  pressed?: boolean;
  /** 等存檔之類的短暫不能按（不用講原因） */
  disabled?: boolean;
}) {
  const blocked = !!reason;
  return (
    <button
      type="button"
      className={(className ?? '') + (blocked ? ' is-blocked' : '')}
      aria-disabled={blocked || undefined}
      aria-pressed={pressed}
      aria-label={label}
      disabled={disabled}
      title={reason ?? title}
      onClick={() => { if (reason) toast.info(reason); else onClick(); }}
    >
      {children}
    </button>
  );
}

/** 小「?」＋點開的說明：一定要成對放，所以回傳按鈕和內容 */
export function useTip(text: ReactNode | null | undefined, label: string): { button: ReactNode; body: ReactNode } {
  const [open, setOpen] = useState(false);
  if (!text) return { button: null, body: null };
  return {
    button: (
      <button
        type="button"
        className={'adjust-tip-btn' + (open ? ' is-on' : '')}
        aria-expanded={open}
        aria-label={`${label}的說明`}
        title={`${label}的說明`}
        onClick={() => setOpen(o => !o)}
      ><Icon icon={CircleHelp} size="xs" /></button>
    ),
    body: open ? <div className="adjust-tip">{text}</div> : null,
  };
}

/**
 * 可拖的數字（秒）：左右拖就改值（Shift 拖得快），點一下變輸入框。沒設時顯示 placeholder（跟著 cue）。
 * 拖的時候 onLive 一直叫（畫面即時跟著），onBegin / onEnd 包起來算一步復原；打字的走 onType（空白 = null）。
 */
export function ScrubNumber({ label, value, placeholder, base, changed, badText, onBegin, onLive, onEnd, onType, unit = '秒', parse = parseTimingSeconds, scrub = scrubSeconds, hideLabel }: {
  label: string;
  value: number | null;
  placeholder: string;
  /** 沒設時從幾秒開始拖 */
  base: number;
  changed?: boolean;
  badText: string;
  onBegin?: () => void;
  onLive: (v: number) => void;
  onEnd?: () => void;
  onType: (v: number | null) => void;
  /** 單位字（預設「秒」）；機關的位置用 m / ° */
  unit?: string;
  /** 打字的解析（預設：0~3600 秒，空白 = null） */
  parse?: (text: string) => { ok: true; value: number | null } | { ok: false };
  /** 拖 dx 像素後的值（預設：秒） */
  scrub?: (start: number, dx: number, coarse: boolean) => number;
  /** 不顯示欄位名稱（旁邊已有標題時） */
  hideLabel?: boolean;
}) {
  const [text, setText] = useState<string | null>(null);
  const [bad, setBad] = useState(false);
  const press = useRef<{ x: number; start: number; moved: boolean } | null>(null);
  const shown = value === null ? '' : String(Math.round(value * 1000) / 1000);
  const commit = () => {
    if (text === null) return;
    const p = parse(text.trim().replace(new RegExp(`${unit}$`, 'u'), ''));
    setText(null);
    if (!p.ok) { setBad(true); return; }
    setBad(false);
    if (p.value !== value) onType(p.value);
  };
  const open = () => { setText(shown); setBad(false); };
  return (
    <div className={'adjust-timing__field' + (changed ? ' is-changed' : '')}>
      {!hideLabel && <span>{label}</span>}
      {text !== null ? (
        <span className="adjust-timing__input">
          <input
            type="text"
            inputMode="decimal"
            autoFocus
            value={text}
            placeholder={placeholder}
            aria-label={label}
            onFocus={(e) => e.target.select()}
            onChange={(e) => setText(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setText(null); }}
          />
          <em>{unit}</em>
        </span>
      ) : (
        <button
          type="button"
          className={'adjust-scrub' + (value === null ? ' is-empty' : '') + (bad ? ' is-bad' : '')}
          aria-label={`${label}：${value === null ? placeholder : `${shown} ${unit}`}`}
          title={unit === '秒' ? '左右拖改秒數（Shift 拖得快），點一下打數字，清空就跟著 cue' : `左右拖改數字（Shift 拖得快），點一下打數字`}
          onPointerDown={(e) => {
            if (e.button > 0) return;
            try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* 已經放開 */ }
            press.current = { x: e.clientX, start: value ?? base, moved: false };
          }}
          onPointerMove={(e) => {
            const p = press.current;
            if (!p) return;
            const dx = e.clientX - p.x;
            if (!p.moved) {
              if (Math.abs(dx) < SCRUB_THRESHOLD_PX) return;
              p.moved = true;
              onBegin?.();
            }
            onLive(scrub(p.start, dx, e.shiftKey));
          }}
          onPointerUp={() => {
            const p = press.current;
            press.current = null;
            if (!p) return;
            if (p.moved) onEnd?.(); else open();
          }}
          onPointerCancel={() => { if (press.current?.moved) onEnd?.(); press.current = null; }}
          // 鍵盤（Enter / 空白鍵）：滑鼠和觸控在放開時就處理好了
          onClick={(e) => { if (e.detail === 0) open(); }}
        >{value === null ? placeholder : <>{shown}<em>{unit}</em></>}</button>
      )}
      {bad && <strong className="adjust-timing__bad" role="alert">{badText}</strong>}
    </div>
  );
}

/** 面板的一段 */
export function PanelSection({ id, title, tip, summary, children }: {
  id: SectionId;
  title: string;
  /** 點「?」才看得到的解釋 */
  tip?: ReactNode;
  /** 收起來時標題右邊的值 */
  summary?: ReactNode;
  children: ReactNode;
}) {
  const userId = useViewAsUser()?.id ?? null;
  const map = useSyncExternalStore(subscribeSections, () => sectionMap(userId));
  const open = isSectionOpen(map, id);
  const t = useTip(tip, title);
  return (
    <section className={'adjust-sec' + (open ? ' is-open' : '')}>
      <div className="adjust-sec__head">
        <button type="button" className="adjust-sec__toggle" aria-expanded={open} onClick={() => setSectionOpen(userId, id, !open)}>
          <Icon icon={ChevronRight} size="xs" className="adjust-sec__chev" />
          <span>{title}</span>
        </button>
        {t.button}
        {!open && summary != null && <span className="adjust-sec__summary">{summary}</span>}
      </div>
      {t.body}
      {open && <div className="adjust-sec__body">{children}</div>}
    </section>
  );
}
